import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Chip, CircularProgress,
  Alert, Divider, IconButton, Collapse
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';

const API = process.env.REACT_APP_API_URL || '';

const fieldSx = {
  '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'var(--c-primario)' } },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-acento)' },
};
const inputStyle = { style: { color: '#e0e0e0' } };
const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };

// ── TagInput ─────────────────────────────────────────────────────────────────
function TagInput({ label, tags, onChange, placeholder }) {
  const [input, setInput] = useState('');
  function agregar() {
    const val = input.trim();
    if (!val || tags.includes(val) || tags.length >= 30) return;
    onChange([...tags, val]);
    setInput('');
  }
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
        <TextField size="small" value={input} placeholder={placeholder || 'Escribí y presioná Enter'}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
          inputProps={inputStyle} sx={{ flex: 1, ...fieldSx }} />
        <Button variant="outlined" onClick={agregar} size="small"
          sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', minWidth: 36 }}>+</Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.map((t, i) => (
          <Chip key={i} label={t} size="small"
            onDelete={() => onChange(tags.filter((_, j) => j !== i))}
            sx={{ bgcolor: 'var(--c-primario)', color: '#e0e0e0', fontSize: 12 }} />
        ))}
      </Box>
    </Box>
  );
}

// ── Tarjeta evolución anterior ───────────────────────────────────────────────
function EvCard({ ev }) {
  const [open, setOpen] = useState(false);
  const fecha = new Date(ev.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const meds = ev.medicamentos ? (typeof ev.medicamentos === 'string' ? JSON.parse(ev.medicamentos) : ev.medicamentos) : [];
  const estudios = ev.estudios ? (typeof ev.estudios === 'string' ? JSON.parse(ev.estudios) : ev.estudios) : [];
  const preview = ev.texto?.replace(/<[^>]*>/g, '').substring(0, 100) + (ev.texto?.length > 100 ? '...' : '');

  return (
    <Box sx={{ border: '1px solid var(--c-primario)', borderRadius: 1.5, mb: 1, opacity: ev.anulada ? 0.5 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, cursor: 'pointer', gap: 1 }}
        onClick={() => setOpen(!open)}>
        <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 700, flexShrink: 0 }}>{fecha}</Typography>
        {ev.anulada && <Chip label="ANULADA" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontSize: 10, height: 16 }} />}
        {!open && <Typography sx={{ fontSize: 13, color: '#b0b0b0', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</Typography>}
        <IconButton size="small" sx={{ color: 'var(--c-acento)', ml: 'auto', p: 0 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />
          <Typography sx={{ fontSize: 13, color: '#e0e0e0', whiteSpace: 'pre-wrap', lineHeight: 1.7, mb: 1 }}>{ev.texto}</Typography>
          {ev.diagnostico && (
            <Typography sx={{ fontSize: 12, color: '#90a4ae', mb: 1 }}>
              <strong style={{ color: 'var(--c-acento)' }}>Diagnóstico:</strong> {ev.diagnostico}
            </Typography>
          )}
          {meds.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 11, color: 'var(--c-acento)', mb: 0.5 }}>Medicamentos:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {meds.map((m, i) => <Chip key={i} label={m} size="small" sx={{ bgcolor: '#1b5e20', color: '#c8e6c9', fontSize: 11 }} />)}
              </Box>
            </Box>
          )}
          {estudios.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11, color: 'var(--c-acento)', mb: 0.5 }}>Estudios:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {estudios.map((e, i) => <Chip key={i} label={e} size="small" sx={{ bgcolor: '#0d47a1', color: '#bbdefb', fontSize: 11 }} />)}
              </Box>
            </Box>
          )}
          <Typography sx={{ fontSize: 11, color: '#666', mt: 1 }}>
            {ev.cargado_por_nombre}{ev.cargado_por_matricula && ` · Mat. ${ev.cargado_por_matricula}`}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function Consulta() {
  const { turnoId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [turno, setTurno] = useState(null);
  const [paciente, setPaciente] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [evoluciones, setEvoluciones] = useState([]);

  // Formulario de evolución
  const [texto, setTexto] = useState('');
  const [diagnostico, setDiagnostico] = useState('');
  const [medicamentos, setMedicamentos] = useState([]);
  const [estudios, setEstudios] = useState([]);
  const [palabrasClave, setPalabrasClave] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  useEffect(() => { cargar(); }, [turnoId]);

  async function cargar() {
    setLoading(true);
    try {
      // Cargar turno
      const turnoRes = await axios.get(`${API}/api/turnos/${turnoId}`);
      const t = turnoRes.data;
      setTurno(t);

      if (t.paciente_id) {
        // Cargar datos del paciente, ficha y evoluciones en paralelo
        const [pacRes, fichaRes, evRes] = await Promise.all([
          axios.get(`${API}/api/pacientes/${t.paciente_id}`).catch(() => ({ data: null })),
          axios.get(`${API}/api/pacientes/${t.paciente_id}/ficha`).catch(() => ({ data: null })),
          axios.get(`${API}/api/pacientes/${t.paciente_id}/evoluciones`).catch(() => ({ data: [] })),
        ]);
        setPaciente(pacRes.data);
        setFicha(fichaRes.data);
        setEvoluciones(evRes.data || []);
      }
    } catch (err) {
      setError('No se pudo cargar la consulta.');
    } finally {
      setLoading(false);
    }
  }

  function calcularEdad(fechaNac) {
    if (!fechaNac) return null;
    const hoy = new Date();
    const nac = new Date(fechaNac);
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad;
  }

  function payloadTurno(estadoNuevo) {
    return {
      medico_id: turno.medico_id,
      paciente_id: turno.paciente_id || null,
      paciente_dni: turno.paciente_dni || '',
      paciente_nombre: turno.paciente_nombre || '',
      paciente_email: turno.paciente_email || '',
      paciente_telefono: turno.paciente_telefono || '',
      motivo: turno.motivo || '',
      fecha: String(turno.fecha).split('T')[0],
      hora_inicio: String(turno.hora_inicio).substring(0, 5),
      duracion: turno.duracion || 30,
      estado: estadoNuevo,
      notas: turno.notas || '',
      sobreturno: turno.sobreturno || 0,
      mensaje_paciente: turno.mensaje_paciente || null,
      nota_medico: turno.nota_medico || null,
    };
  }

  async function guardarEvolucion(marcarAtendido = false) {
    if (!texto.trim()) { setError('Escribí algo en la evolución antes de guardar.'); return; }
    setGuardando(true);
    setError('');
    try {
      await axios.post(`${API}/api/pacientes/${turno.paciente_id}/evoluciones`, {
        texto,
        diagnostico: diagnostico || null,
        medicamentos,
        estudios,
        palabras_clave: palabrasClave.length ? palabrasClave : null,
        turno_id: parseInt(turnoId),
      });
      if (marcarAtendido) {
        await axios.put(`${API}/api/turnos/${turnoId}`, payloadTurno('atendido'));
        navigate(-1);
      } else {
        setTexto('');
        setDiagnostico('');
        setMedicamentos([]);
        setEstudios([]);
        setPalabrasClave([]);
        setExito('Evolución guardada correctamente.');
        setTimeout(() => setExito(''), 3000);
        cargar();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setGuardando(false);
    }
  }

  async function descargarPDF() {
    setDescargandoPdf(true);
    try {
      const r = await axios.get(`${API}/api/turnos/${turnoId}/consulta.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consulta-${turnoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Error descargando PDF:', err); }
    finally { setDescargandoPdf(false); }
  }

  async function soloAtendido() {
    try {
      await axios.put(`${API}/api/turnos/${turnoId}`, payloadTurno('atendido'));
      navigate(-1);
    } catch { navigate(-1); }
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  if (!turno) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">No se encontró el turno.</Alert>
      <Button onClick={() => navigate(-1)} sx={{ mt: 2, color: 'var(--c-acento)' }} startIcon={<ArrowBackIcon />}>Volver</Button>
    </Box>
  );

  const edad = calcularEdad(paciente?.fecha_nacimiento);
  const nombrePaciente = turno.paciente_nombre || `Paciente`;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', pb: 6 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: 'var(--c-acento)', flexShrink: 0 }}>
          Volver
        </Button>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
            {nombrePaciente}
            {edad !== null && <Typography component="span" sx={{ fontSize: 16, color: '#90a4ae', ml: 1 }}>{edad} años</Typography>}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            {turno.paciente_dni && <Typography sx={{ fontSize: 13, color: '#90a4ae' }}>DNI: {turno.paciente_dni}</Typography>}
            {paciente?.obra_social && <Chip label={paciente.obra_social} size="small" sx={{ bgcolor: 'var(--c-primario)', color: 'var(--c-acento)', fontSize: 12 }} />}
            {turno.motivo && <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>· {turno.motivo}</Typography>}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 13, color: '#90a4ae' }}>
            {String(turno.fecha).split('T')[0].split('-').reverse().join('/')} · {String(turno.hora_inicio).substring(0, 5)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#666', mb: 0.5 }}>{turno.medico_nombre}</Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {turno.paciente_id && (
              <Button size="small" variant="outlined" startIcon={<MedicalInformationIcon sx={{ fontSize: 15 }} />}
                onClick={() => window.open(`/historial/${turno.paciente_id}`, '_blank')}
                sx={{ color: '#81c784', borderColor: '#81c784', fontSize: 11, py: 0.3 }}>
                Ver HC
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon sx={{ fontSize: 15 }} />}
              onClick={descargarPDF} disabled={descargandoPdf}
              sx={{ color: '#ef9a9a', borderColor: '#ef9a9a', fontSize: 11, py: 0.3 }}>
              {descargandoPdf ? 'Generando...' : 'Exportar PDF'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Alertas clínicas ── */}
      {ficha?.alergias && (
        <Alert severity="warning" sx={{ mb: 1.5, fontWeight: 700 }}>
          ⚠️ ALERGIAS: {ficha.alergias}
        </Alert>
      )}
      {ficha?.medicacion_habitual && (
        <Alert severity="info" sx={{ mb: 1.5, '& .MuiAlert-message': { color: '#e0e0e0' }, bgcolor: 'rgba(66,165,245,0.1)', border: '1px solid #1565c0' }}>
          💊 Medicación habitual: {ficha.medicacion_habitual}
        </Alert>
      )}
      {ficha?.enfermedades_cronicas && (
        <Alert severity="info" sx={{ mb: 1.5, '& .MuiAlert-message': { color: '#e0e0e0' }, bgcolor: 'rgba(100,100,100,0.1)', border: '1px solid #555' }}>
          🩺 Enfermedades crónicas: {ficha.enfermedades_cronicas}
        </Alert>
      )}
      {!turno.paciente_id && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Este paciente no está registrado en el sistema — no se pueden cargar evoluciones ni ver historial.
        </Alert>
      )}

      {/* ── Evoluciones anteriores ── */}
      {evoluciones.length > 0 && (
        <Box sx={{ mb: 3, bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2, border: '1px solid var(--c-primario)' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--c-acento)', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Historial — últimas {Math.min(evoluciones.length, 5)} evoluciones
          </Typography>
          {evoluciones.slice(0, 5).map(ev => <EvCard key={ev.id} ev={ev} />)}
          {evoluciones.length > 5 && (
            <Button size="small" onClick={() => window.open(`/historial/${turno.paciente_id}`, '_blank')}
              sx={{ color: 'var(--c-acento)', fontSize: 12, mt: 0.5 }}>
              Ver todas las evoluciones →
            </Button>
          )}
        </Box>
      )}

      {/* ── Nueva evolución ── */}
      {turno.paciente_id && (
        <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2.5, border: '1px solid var(--c-primario)' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--c-acento)', mb: 2, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Nueva evolución
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Evolución *"
              multiline rows={7}
              value={texto}
              onChange={e => setTexto(e.target.value)}
              placeholder="Escribí la evolución clínica del paciente..."
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} fullWidth />

            <TextField
              label="Diagnóstico"
              value={diagnostico}
              onChange={e => setDiagnostico(e.target.value)}
              placeholder="Diagnóstico presuntivo o definitivo..."
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} fullWidth />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TagInput label="Medicamentos indicados" tags={medicamentos} onChange={setMedicamentos} placeholder="Ej: Ibuprofeno 400mg" />
              <TagInput label="Estudios solicitados" tags={estudios} onChange={setEstudios} placeholder="Ej: Hemograma" />
            </Box>

            <TagInput label="Palabras clave" tags={palabrasClave} onChange={setPalabrasClave} placeholder="Ej: HTA, diabetes" />

            {error && <Alert severity="error">{error}</Alert>}
            {exito && <Alert severity="success">{exito}</Alert>}

            {/* Botones */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pt: 1 }}>
              <Button variant="outlined" onClick={soloAtendido}
                sx={{ color: '#81c784', borderColor: '#2e7d32', '&:hover': { borderColor: '#81c784' } }}>
                Solo marcar atendido
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />}
                onClick={() => guardarEvolucion(false)} disabled={guardando}
                sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
                {guardando ? 'Guardando...' : 'Guardar evolución'}
              </Button>
              <Button variant="contained" startIcon={<CheckCircleIcon />}
                onClick={() => guardarEvolucion(true)} disabled={guardando}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
                {guardando ? 'Guardando...' : 'Guardar y marcar atendido'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Si no tiene paciente vinculado, solo botón de atendido */}
      {!turno.paciente_id && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={soloAtendido}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
            Marcar como atendido
          </Button>
        </Box>
      )}
    </Box>
  );
}
